/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApplicationRef, inject, Provider } from '@angular/core';
import { ApolloClient, ApolloLink, InMemoryCache, PossibleTypesMap, TypePolicies } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { MsalService } from '@azure/msal-angular';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { first, firstValueFrom } from 'rxjs';
import { GraphQlErrorInterceptor } from './interceptors/graphql-error/graphql-error.interceptor';

export const graphql = '/api/graphql';
export const minutesTTL = 15;

const possibleTypes: PossibleTypesMap = {
  // Queries that will be cached with children queries
  Cached: ['TwinQuery', 'PanelsQuery', 'AssetsQuery', 'AlarmsQuery', 'UtilityContractsV2Query'],
  // To exclude query from caching if parent query is cached
  NotCached: ['UserQuery', 'PanelsDataSpecialQuery'],
};

const typePolicies: TypePolicies = {
  // Super types
  Cached: { merge: mergeObjects },
  NotCached: { merge: noCache },
  // Overrides
  Organization: { fields: { hierarchy: { merge: noCache } } },
  ApplicationUserViewModel: { fields: { roles: { merge: mergeRoles } } },
  AssetsQuery: { fields: { assetCategories: { merge: noCache }, assetDefinitions: { merge: noCache } } },
  HistoryQuery: { fields: { historyData: { merge: noCache } } },
  GetAlarmOptionsForAssetResponse: { fields: { tagOptions: { merge: noCache } } },
  ConfigurationView: { keyFields: false },
  ConfigurationSection: { keyFields: false },
  ConfigurationField: { keyFields: false },
  ConfigurationRelationGroup: { keyFields: false },
  ConfigurationAction: { keyFields: false },
  ConfigurationMapItem: { keyFields: false },
  ConfigurationPointValue: { keyFields: false },
};

function mergeObjects(existing: any = {}, incoming: any): any {
  return {
    ...existing,
    ...Object.keys(incoming).reduce((previous, current) => {
      // Check if both existing and incoming values are objects
      if (
        existing[current] &&
        incoming[current] &&
        typeof existing[current] === 'object' &&
        !Array.isArray(existing[current]) &&
        typeof incoming[current] === 'object' &&
        !Array.isArray(incoming[current])
      ) {
        previous[current] = mergeObjects(existing[current], incoming[current]); // Recursive merge for nested objects
      } else {
        previous[current] = incoming[current]; // Use the incoming value for non-objects
      }
      return previous;
    }, {} as any),
    __cacheTimestamp: existing.__cacheTimestamp ?? Date.now(), // Add insertion time for TTL cache pruning
  };
}

function noCache<T>(_existing: T, incoming: T): T {
  return incoming;
}

function mergeRoles(existing: any[] = [], incoming: any[] = []): any[] {
  const existingByName = new Map(existing.map((role) => [role.name, role]));

  return incoming.map((role) => ({ ...existingByName.get(role.name), ...role }));
}

export function createApollo(httpLink: HttpLink, msalService: MsalService): ApolloClient.Options {
  const defaultLink = httpLink.create({
    uri: graphql,
    extractFiles: (body) => extractFiles(body, isExtractableFile), // To handle file uploads
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: graphql,
      shouldRetry(errOrCloseEvent) {
        console.error(errOrCloseEvent);
        return true;
      },
      connectionParams: async () => {
        const account = msalService.instance.getAllAccounts()[0];
        if (!account) {
          return {};
        }
        const response = await firstValueFrom(
          msalService.acquireTokenSilent({
            scopes: ['openid', 'profile', 'email'],
            account: account,
          }),
        );
        return response ? { authorization: `Bearer ${response.idToken}` } : {};
      },
    }),
  );

  const link = ApolloLink.split(
    // Choose link based on operation type
    // Use WebSocket link for subscriptions and the default link for queries and mutations
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION;
    },
    wsLink,
    defaultLink,
  );

  const cache = new InMemoryCache({ possibleTypes, typePolicies });

  inject(ApplicationRef)
    .isStable.pipe(first((stable) => stable))
    .subscribe(() => setInterval(() => pruneCache(cache), 60 * 1000));

  return { link, cache, defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } } };
}

export function provideApollo(): Provider[] {
  return [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, MsalService, ApplicationRef],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GraphQlErrorInterceptor,
      multi: true,
    },
    Apollo,
  ];
}

function pruneCache(cache: InMemoryCache): void {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      panels(existing) {
        if (!existing) return undefined;

        const result = { ...existing };

        Object.keys(existing).forEach((key) => {
          const match = key.match(/^.+\(\{.*"endDate":"([^"]+)",.*\}\)$/);
          if (match) {
            const now = Date.now();
            const endDate = new Date(match[1]);
            const cacheTimestamp = existing[key]?.__cacheTimestamp;

            if (endDate && cacheTimestamp) {
              const isToday = endDate.getTime() > now;
              const isStale = Date.now() - cacheTimestamp > minutesTTL * 60 * 1000;
              if (isToday && isStale) {
                delete result[key];
              }
            }
          }
        });

        return result;
      },
    },
  });
  cache.gc();
}
